import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getVolumeStyleObject } from './volume.style';
import { useStyles } from '../../../../common/hooks/useStyles';
import { playerSettingsSelector } from '../../../../common/store/selectors/playerSettings.selector';
import {
  changeAdVolume,
  setAdMute,
  setContentMute,
  setUserInteraction,
  setVolume as changeContentVolume,
} from '../../store/controlsPlugin.actions';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import { adsDataSelector } from '../../../../common/store/selectors/adsData.selector';
import { useHoverRef } from '../../../../common/hooks/useHoverRef';
import { css } from '../../../../common/styles/styles.utils';
import { getVolumeBarId } from '../../htmlElements/htmlElementsIdGetter';
import { splitViewSelector } from '../../../../common/store/selectors/splitView.selector';
import { isDesktopMode, isEmpty } from '../../../../common/utils/utils';
import { brandingDataSelector } from '../../../../common/store/selectors/brandingData.selector';
import { VolumeIcon } from './volumeIcon/volumeIcon';

const getOffsetY = (mouseEvent: any): number => {
  const rect = mouseEvent.currentTarget.getBoundingClientRect();
  return mouseEvent.clientY - Math.round(rect.top);
};

interface VolumeComponentProps {
  outsideControlsBar?: boolean;
}

export const Volume: FunctionComponent<VolumeComponentProps> = props => {
  const { outsideControlsBar } = props;

  const dispatch = useDispatch();
  const volume = useSelector(playerSettingsSelector.volume);
  const contentMuted = useSelector(playerSettingsSelector.muted);
  const adMuted = useSelector(adsDataSelector.adMuted);
  const playerMode = useSelector(playerDataSelector.playerMode);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);
  const splitViewRatio = useSelector(splitViewSelector.splitViewRatio);
  const brandingColor = useSelector(brandingDataSelector.brandingColor);
  const showControls = useSelector(playerDataSelector.showControls);

  const showAdControls = isVastAd && adBreak;
  const desktopMode = isDesktopMode(playerMode);
  const splitView = !isEmpty(splitViewRatio);

  const [showVolumeBar, setShowVolumeBar] = useState(false);
  const [isHoveredVolumeContainer, setHoveredVolumeContainer] = useState(false);
  const [isVolumeBarMousePressedDown, setVolumeBarMousePressedDown] = useState(false);

  const volumeBar = useRef<HTMLDivElement>(null);
  const volumeProgressBar = useRef<HTMLDivElement>(null);
  const [volumeIconRef, volumeIconHovered] = useHoverRef();

  const muted = showAdControls ? adMuted : contentMuted;
  const setMute = showAdControls ? setAdMute : setContentMute;
  const changeVolume = showAdControls ? changeAdVolume : changeContentVolume;

  const styles = useStyles(getVolumeStyleObject, [playerSize, showVolumeBar, muted, desktopMode, splitView, brandingColor, showControls, showAdControls, outsideControlsBar]);
  const { volumeContainerStyle, volumeBarContainerWrapperStyle, volumeBarContainerStyle, volumeBarStyle, volumeBarBackgroundStyle, volumeProgressBarStyle } = styles;

  useEffect(() => {
    volumeProgressBar.current.style.height = (`${volume * 100}%`);
  }, [volume]);

  const updateUserInteraction = (newMuteState: boolean): void => {
    if (!showAdControls) {
      dispatch(setUserInteraction(newMuteState ? 'mute' : 'unmute'));
    }
  };

  const onMouseEnterVolumeContainer = (): void => {
    if (desktopMode) {
      setHoveredVolumeContainer(true);
    }
  };

  const onMouseLeaveVolumeContainer = (): void => {
    if (desktopMode && !outsideControlsBar) {
      setHoveredVolumeContainer(false);
      setShowVolumeBar(false);
    }
  };

  const onChangeVolume = (mouseEvent: any): void => {
    const offsetY = getOffsetY(mouseEvent);
    if (offsetY > 0 && offsetY < volumeBar.current.clientHeight) {
      const percent = 1 - (offsetY / volumeBar.current.clientHeight);
      volumeProgressBar.current.style.height = (`${percent * 100}%`);
      dispatch(changeVolume(percent));
    }
  };

  const onMouseDownVolumeBar = (mouseEvent: any): void => {
    setVolumeBarMousePressedDown(true);
    if (muted) {
      dispatch(setMute(false));
      updateUserInteraction(false);
    }
    onChangeVolume(mouseEvent);
  };

  const onMouseUpVolumeBar = (): void => {
    setVolumeBarMousePressedDown(false);
  };

  const onMouseMoveVolumeBar = (mouseEvent: any): void => {
    if (isVolumeBarMousePressedDown) {
      onChangeVolume(mouseEvent);
    }
  };

  return (
    <div
      onMouseEnter={onMouseEnterVolumeContainer}
      onMouseLeave={onMouseLeaveVolumeContainer}
      style={css(volumeContainerStyle)}
      ref={volumeIconRef}
    >
      <div style={css(volumeBarContainerWrapperStyle)}>
        <div style={css(volumeBarContainerStyle)}>
          <div
            ref={volumeBar}
            id={getVolumeBarId(playerUniqueId)}
            style={css(volumeBarStyle)}
            onMouseDown={onMouseDownVolumeBar}
            onMouseUp={onMouseUpVolumeBar}
            onMouseMove={onMouseMoveVolumeBar}
            onMouseLeave={onMouseUpVolumeBar}
          >
            <div style={volumeBarBackgroundStyle} />
            <div style={css(volumeProgressBarStyle)} ref={volumeProgressBar} />
          </div>
        </div>
      </div>
      <VolumeIcon
        isHoveredVolumeContainer={isHoveredVolumeContainer}
        volumeIconHovered={volumeIconHovered}
        volumeProgressBar={volumeProgressBar.current}
        setShowVolumeBar={setShowVolumeBar}
        outsideControlsBar={outsideControlsBar}
      />
    </div>
  );
};
